import * as THREE from 'three'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'
import type { GSplatShaderLanguage } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import {
  GSPLAT_RELIGHTING_MESH_OUTPUT_GLSL,
  GSPLAT_RELIGHTING_MESH_OUTPUT_WGSL,
  GSPLAT_RELIGHTING_SPLAT_FRAGMENT_GLSL,
  GSPLAT_RELIGHTING_SPLAT_FRAGMENT_WGSL,
} from './GSplatRelightingShaders'

export interface GSplatRelightingMaterial {
  getShaderChunks(language: GSplatShaderLanguage): Map<string, string>
  update(): void
}

export interface GSplatRelightingMaterialLease {
  release(): void
}

export interface GSplatRelightingOptions extends GSplatShaderEffectOptions {
  textureScale?: number
  priority?: number
  layerName?: string
  blend?: number
  brightness?: number
  background?: number
  layer?: unknown
  texture?: unknown
  screenSize?: THREE.Vector4
}

export interface GSplatRelightingInitializeInput {
  readonly layer?: unknown
  readonly texture?: unknown
  readonly material?: GaussianSplatMaterial | null
}

/**
 * Relighting 的 backend-neutral 参数与双语言 shader effect。
 *
 * 每 View 的 layer、target、proxy camera 和 resource lease 由后续 System controller 持有；
 * 这个对象只负责冻结 PlayCanvas 的六个 public 参数、fragment 数学和 proxy material output
 * chunk，避免把 target 生命周期混进普通 material effect。
 */
export class GSplatRelighting extends GSplatShaderEffect {
  private textureScaleValue: number
  private priorityValue: number
  private layerNameValue: string
  private blendValue: number
  private brightnessValue: number
  private backgroundValue: number

  private _layer: unknown
  private _texture: unknown
  private readonly _screenSize: THREE.Vector4
  private readonly changeListeners = new Set<() => void>()
  private revisionValue = 0
  private _initialized = false

  constructor(options: GSplatRelightingOptions = {}) {
    super({ ...options, id: options.id ?? 'relighting' })
    this.textureScaleValue = options.textureScale ?? 1
    this.priorityValue = options.priority ?? -1
    this.layerNameValue = options.layerName ?? 'Relighting'
    this.blendValue = options.blend ?? 1
    this.brightnessValue = options.brightness ?? 2
    this.backgroundValue = options.background ?? 1
    this._layer = options.layer ?? null
    this._texture = options.texture ?? null
    this._screenSize = options.screenSize?.clone() ?? new THREE.Vector4(1, 1, 1, 1)
    this.validateParameters()
  }

  get textureScale(): number {
    return this.textureScaleValue
  }

  set textureScale(value: number) {
    assertRange('textureScale', value, 0.1, 1)
    if (this.textureScaleValue === value) return
    this.textureScaleValue = value
    this.notifyChanged()
  }

  get priority(): number {
    return this.priorityValue
  }

  set priority(value: number) {
    if (!Number.isFinite(value)) {
      throw new Error(`GSplatRelighting priority 必须为有限数值: ${value}`)
    }
    if (this.priorityValue === value) return
    this.priorityValue = value
    this.notifyChanged()
  }

  get layerName(): string {
    return this.layerNameValue
  }

  set layerName(value: string) {
    if (!value.trim()) throw new Error('GSplatRelighting layerName 不能为空')
    if (this.layerNameValue === value) return
    this.layerNameValue = value
    this.notifyChanged()
  }

  get blend(): number {
    return this.blendValue
  }

  set blend(value: number) {
    assertRange('blend', value, 0, 1)
    if (this.blendValue === value) return
    this.blendValue = value
    this.notifyChanged()
  }

  get brightness(): number {
    return this.brightnessValue
  }

  set brightness(value: number) {
    assertRange('brightness', value, 0, 5)
    if (this.brightnessValue === value) return
    this.brightnessValue = value
    this.notifyChanged()
  }

  get background(): number {
    return this.backgroundValue
  }

  set background(value: number) {
    assertRange('background', value, 0, 5)
    if (this.backgroundValue === value) return
    this.backgroundValue = value
    this.notifyChanged()
  }

  get revision(): number {
    return this.revisionValue
  }

  subscribe(listener: () => void): () => void {
    this.changeListeners.add(listener)
    return () => this.changeListeners.delete(listener)
  }

  get layer(): unknown {
    return this._layer
  }

  get texture(): unknown {
    return this._texture
  }

  get initialized(): boolean {
    return this._initialized
  }

  initialize(input: GSplatRelightingInitializeInput = {}): boolean {
    this.validateParameters()
    if (input.layer !== undefined) this._layer = input.layer
    if (input.texture !== undefined) this._texture = input.texture
    if (input.material !== undefined) this.setMaterial(input.material)
    this._initialized = true
    if (this.enabled) return this.applyShaders()
    return false
  }

  setLayer(layer: unknown): void {
    if (this._layer === layer) return
    this._layer = layer
    this.notifyChanged()
  }

  setTexture(texture: unknown): void {
    if (this._texture === texture) return
    this._texture = texture
    this.setUniform('uRelightMap', texture)
    this.notifyChanged()
  }

  setScreenSize(width: number, height: number): void {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw new Error(`GSplatRelighting screen size 非法: ${width}x${height}`)
    }
    this._screenSize.set(width, height, 1 / width, 1 / height)
    this.setUniform('uScreenSize', this._screenSize)
    this.notifyChanged()
  }

  /**
   * 为 proxy mesh material 同时安装 GLSL/WGSL outputPS，并以一次 update 发布。
   * 任一语言 chunk map 不可用或 update 失败时恢复原值。
   */
  configureMaterial(material: GSplatRelightingMaterial): GSplatRelightingMaterialLease {
    const glsl = material.getShaderChunks('glsl')
    const wgsl = material.getShaderChunks('wgsl')
    const previousGLSL = glsl.get('outputPS')
    const previousWGSL = wgsl.get('outputPS')
    try {
      glsl.set('outputPS', GSPLAT_RELIGHTING_MESH_OUTPUT_GLSL)
      wgsl.set('outputPS', GSPLAT_RELIGHTING_MESH_OUTPUT_WGSL)
      material.update()
    } catch (error) {
      restoreChunk(glsl, 'outputPS', previousGLSL)
      restoreChunk(wgsl, 'outputPS', previousWGSL)
      throw error
    }
    let released = false
    return {
      release: () => {
        if (released) return
        released = true
        if (glsl.get('outputPS') === GSPLAT_RELIGHTING_MESH_OUTPUT_GLSL) {
          restoreChunk(glsl, 'outputPS', previousGLSL)
        }
        if (wgsl.get('outputPS') === GSPLAT_RELIGHTING_MESH_OUTPUT_WGSL) {
          restoreChunk(wgsl, 'outputPS', previousWGSL)
        }
        material.update()
      },
    }
  }

  validateParameters(): void {
    if (
      !Number.isFinite(this.textureScaleValue) ||
      this.textureScaleValue < 0.1 ||
      this.textureScaleValue > 1
    ) {
      throw new Error(`GSplatRelighting textureScale 必须位于 [0.1, 1]: ${this.textureScale}`)
    }
    if (!Number.isFinite(this.priorityValue)) {
      throw new Error(`GSplatRelighting priority 必须为有限数值: ${this.priority}`)
    }
    if (!this.layerNameValue.trim()) {
      throw new Error('GSplatRelighting layerName 不能为空')
    }
    assertRange('blend', this.blendValue, 0, 1)
    assertRange('brightness', this.brightnessValue, 0, 5)
    assertRange('background', this.backgroundValue, 0, 5)
  }

  getFragmentShaderGLSL(): string {
    return GSPLAT_RELIGHTING_SPLAT_FRAGMENT_GLSL
  }

  getFragmentShaderWGSL(): string {
    return GSPLAT_RELIGHTING_SPLAT_FRAGMENT_WGSL
  }

  protected updateEffect(): void {
    if (!this._initialized) return
    this.validateParameters()
    this.setUniform('uRelightMap', this._texture)
    this.setUniform('uScreenSize', this._screenSize)
    this.setUniform('uRelightBlend', this.blendValue)
    this.setUniform('uRelightBrightness', this.brightnessValue)
    this.setUniform('uRelightBackground', this.backgroundValue)
  }

  override destroy(): void {
    if (this.destroyed) return
    super.destroy()
    this._initialized = false
    this._layer = null
    this._texture = null
    this.changeListeners.clear()
  }

  private notifyChanged(): void {
    this.revisionValue += 1
    for (const listener of this.changeListeners) listener()
    if (this.enabled) this.syncUniforms()
  }
}

function assertRange(name: string, value: number, min: number, max: number): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new Error(`GSplatRelighting ${name} 必须位于 [${min}, ${max}]: ${value}`)
  }
}

function restoreChunk(chunks: Map<string, string>, name: string, value: string | undefined): void {
  if (value === undefined) chunks.delete(name)
  else chunks.set(name, value)
}
